// One vendor: name, decorator tag, website and ship-to address here; order
// email, phone, account details and named contacts in SupplierAccountSection
// (the same block the PO page reads, one cache key ["vendors", id]).
// Pilot 2026-10-13: decorator POs had no address to ship blanks to.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SupplierAccountSection } from "@/components/SupplierAccountSection";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

interface VendorAddress {
  street1: string;
  street2: string | null;
  city: string;
  stateCode: string;
  postalCode: string;
  countryCode: string;
}

interface VendorDetail {
  vendor: {
    id: string;
    name: string;
    source: string;
    website: string | null;
    isDecorator: boolean;
    address: VendorAddress | null;
  };
}

interface Draft {
  name: string;
  website: string;
  isDecorator: boolean;
  street1: string;
  street2: string;
  city: string;
  stateCode: string;
  postalCode: string;
}

const toDraft = (v: VendorDetail["vendor"]): Draft => ({
  name: v.name,
  website: v.website ?? "",
  isDecorator: v.isDecorator,
  street1: v.address?.street1 ?? "",
  street2: v.address?.street2 ?? "",
  city: v.address?.city ?? "",
  stateCode: v.address?.stateCode ?? "",
  postalCode: v.address?.postalCode ?? "",
});

export default function VendorEditPage() {
  const { vendorId = "" } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  // Whoever can add a vendor can edit or remove it (Stephanie 2026-10-30).
  const canEdit = user?.roles.some((r) => r === "admin" || r === "production_rep" || r === "client_rep") ?? false;
  const canRemove = canEdit;
  const { data, isLoading, error } = useQuery<VendorDetail>({
    queryKey: ["vendors", vendorId],
    queryFn: () => api(`/vendors/${vendorId}`),
    enabled: vendorId !== "",
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  // Remove a vendor — e.g. a duplicate (Stephanie 2026-10-16). Two-step:
  // "Remove vendor" then "Yes, remove it". The server refuses while it has
  // open POs, and for catalogue-feed suppliers, and says why.
  const navigate = useNavigate();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const remove = useMutation({
    mutationFn: () => api(`/vendors/${vendorId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["vendors"] });
      navigate("/production/vendors");
    },
  });
  // Read-only until Edit; Save or Cancel closes the fields again
  // (Stephanie 2026-10-14).
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (data?.vendor) setDraft(toDraft(data.vendor));
  }, [data]);

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const v = data!.vendor;
      const hasAddress = [d.street1, d.city, d.stateCode, d.postalCode].some((x) => x.trim() !== "");
      const body: Record<string, unknown> = {
        website: d.website.trim() || null,
        is_decorator: d.isDecorator,
        address: hasAddress
          ? {
              street1: d.street1,
              street2: d.street2.trim() || null,
              city: d.city,
              state_code: d.stateCode,
              postal_code: d.postalCode,
            }
          : null,
      };
      if (v.source === "internal" && d.name.trim() !== v.name) body.name = d.name.trim();
      return api(`/vendors/${vendorId}`, { method: "PATCH", body: JSON.stringify(body) });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["vendors"] });
    },
  });

  if (isLoading || !draft) {
    return <p className="px-5 py-10 text-center text-sm text-muted">{error ? "Couldn't load this vendor." : "Loading…"}</p>;
  }
  const v = data!.vendor;
  const nameLocked = v.source !== "internal";
  const set = (patch: Partial<Draft>): void => setDraft({ ...draft, ...patch });
  const startEdit = (): void => {
    setDraft(toDraft(v));
    save.reset();
    setEditing(true);
  };
  const partialAddress =
    [draft.street1, draft.city, draft.stateCode, draft.postalCode].some((x) => x.trim() !== "") &&
    [draft.street1, draft.city, draft.stateCode, draft.postalCode].some((x) => x.trim() === "");

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          <Link to="/production/vendors" className="hover:underline">Vendors</Link> / {v.name}
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">{v.name}</h1>
      </div>

      {!editing ? (
        <section className="rounded-md border border-rule bg-white p-5 sm:p-[22px]" aria-label="Vendor details">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Vendor</h2>
            {canEdit && (
              <Button type="button" variant="ghost" size="sm" onClick={startEdit}>
                Edit
              </Button>
            )}
          </div>
          <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-muted">Name</dt>
              <dd className="mt-0.5 text-ink">{v.name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted">Website</dt>
              <dd className="mt-0.5 text-ink">
                {v.website ? (
                  <a href={v.website} className="font-semibold hover:underline">
                    {v.website.replace(/^https?:\/\//, "")}
                  </a>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted">Decorator</dt>
              <dd className="mt-0.5 text-ink">{v.isDecorator ? "Yes" : "No"}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted">Address</dt>
              <dd className="mt-0.5 text-ink">
                {v.address ? (
                  <>
                    {v.address.street1}
                    {v.address.street2 ? `, ${v.address.street2}` : ""}
                    <br />
                    {v.address.city}, {v.address.stateCode} {v.address.postalCode}
                  </>
                ) : (
                  <span className="text-risk">No address yet</span>
                )}
              </dd>
            </div>
          </dl>
        </section>
      ) : (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!partialAddress) save.mutate(draft);
        }}
        className="rounded-md border border-rule bg-white p-5 sm:p-[22px]"
      >
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Vendor</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">Name</span>
            <Input
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              disabled={!canEdit || nameLocked}
              maxLength={200}
              title={nameLocked ? "Comes from the supplier's catalogue feed" : undefined}
            />
            {nameLocked && <span className="text-xs text-muted">From the catalogue feed — can't be renamed here.</span>}
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">Website</span>
            <Input value={draft.website} onChange={(e) => set({ website: e.target.value })} disabled={!canEdit} placeholder="https://" />
          </label>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={draft.isDecorator}
            onChange={(e) => set({ isDecorator: e.target.checked })}
            disabled={!canEdit}
            className="h-4 w-4"
          />
          Decorator — offered when a PO's goods go to a decorator
        </label>

        <h3 className="mt-6 text-sm font-semibold text-ink">Address</h3>
        <p className="text-xs text-muted">
          Where goods ship when this vendor decorates them, and what their PO shows.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-6">
          <Input className="sm:col-span-4" value={draft.street1} onChange={(e) => set({ street1: e.target.value })} disabled={!canEdit} placeholder="Street" aria-label="Street" />
          <Input className="sm:col-span-2" value={draft.street2} onChange={(e) => set({ street2: e.target.value })} disabled={!canEdit} placeholder="Suite / unit" aria-label="Suite or unit" />
          <Input className="sm:col-span-3" value={draft.city} onChange={(e) => set({ city: e.target.value })} disabled={!canEdit} placeholder="City" aria-label="City" />
          <Input className="sm:col-span-1" value={draft.stateCode} onChange={(e) => set({ stateCode: e.target.value.toUpperCase() })} disabled={!canEdit} placeholder="State" maxLength={2} aria-label="State" />
          <Input className="sm:col-span-2" value={draft.postalCode} onChange={(e) => set({ postalCode: e.target.value })} disabled={!canEdit} placeholder="ZIP" aria-label="ZIP" />
        </div>

        {canEdit && (
          <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
            {partialAddress && <span className="text-sm text-risk">Fill in street, city, state and ZIP — or clear them all.</span>}
            {save.error && <span className="text-sm text-risk">{apiErrorMessage(save.error, "Couldn't save.")}</span>}
            <Button type="button" variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || partialAddress}>
              {save.isPending ? "Saving…" : "Save vendor"}
            </Button>
          </div>
        )}
      </form>
      )}

      <SupplierAccountSection vendorId={vendorId} canEdit={canEdit} />

      {canRemove && (
        <section className="rounded-md border border-rule bg-white p-5 sm:p-[22px]" aria-label="Remove vendor">
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Remove vendor</h2>
          <p className="mt-1 text-sm text-muted">
            For a duplicate or a vendor you no longer use. It disappears from the lists; its past POs and bills keep their history.
          </p>
          {remove.error && (
            <p role="alert" className="mt-3 text-sm text-risk">{apiErrorMessage(remove.error, "Couldn't remove the vendor.")}</p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {confirmRemove ? (
              <>
                <span className="text-sm font-semibold text-ink">Remove {v.name}?</span>
                <Button type="button" variant="destructive" size="sm" onClick={() => remove.mutate()} disabled={remove.isPending}>
                  {remove.isPending ? "Removing…" : "Yes, remove it"}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setConfirmRemove(false); remove.reset(); }} disabled={remove.isPending}>
                  Keep it
                </Button>
              </>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={() => setConfirmRemove(true)}>
                Remove vendor
              </Button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
